import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

export type AdminStatus = {
  admin: {
    email: string;
    mfa_required: boolean;
    role: "owner" | "admin";
  } | null;
  mfa: {
    expires_at: string | null;
    verified: boolean;
  };
};

export type PaginatedResponse<T> = {
  total_count: number;
} & Record<string, T[]>;

export type DashboardTrends = {
  command_outcomes: { status: string; count: number }[];
  runs_per_day: { date: string; count: number }[];
  user_growth: { date: string; cumulative: number }[];
};

export type DashboardSummary = {
  active_users_7d: number;
  accepted_invitations: number;
  linked_vehicles: number;
  pending_invitations: number;
  recent_commands: Array<{
    command: string | null;
    run_id: string;
    started_at: string | null;
    status: string;
    user_email: string | null;
    workflow_name: string | null;
  }>;
  recent_runs: Array<{
    created_at: string;
    id: string;
    status: string;
    user_email: string | null;
    vehicle_name: string | null;
    workflow_name: string | null;
  }>;
  total_users: number;
  total_workflows: number;
};

export type InvitationRow = {
  accepted_at: string | null;
  accepted_user_email: string | null;
  created_at: string;
  device_label: string | null;
  email: string;
  expires_at: string;
  id: string;
  invited_by_email: string | null;
  last_error: string | null;
  sent_at: string | null;
  status: "pending" | "accepted" | "revoked" | "expired";
};

export type RunRow = {
  created_at: string;
  finished_at: string | null;
  id: string;
  started_at: string | null;
  status: string;
  trigger_type: string;
  user_email: string | null;
  vehicle_name: string | null;
  workflow_name: string | null;
};

export type WorkflowRow = {
  created_at: string;
  enabled: boolean;
  id: string;
  name: string;
  run_count: number;
  step_count: number;
  trigger_type: string;
  updated_at: string;
  user_email: string | null;
};

export type UserDetailData = {
  profile: {
    id: string;
    email: string;
    created_at: string;
    app_role: string;
    tier_id: string | null;
  };
  invitation: {
    id: string;
    status: string;
    created_at: string;
    expires_at: string;
    accepted_at: string | null;
    device_label: string | null;
    last_error: string | null;
    sent_at: string | null;
  } | null;
  vehicles: Array<{
    id: string;
    vin: string;
    display_name: string | null;
    option_codes: string | null;
    exterior_color: string | null;
    wheel_type: string | null;
    is_key_pair_ready: boolean;
    created_at: string;
    last_seen_at: string | null;
  }>;
  workflows: Array<{
    id: string;
    name: string;
    enabled: boolean;
    step_count: number;
    created_at: string;
    updated_at: string;
  }>;
  recent_runs: Array<{
    id: string;
    status: string;
    trigger_type: string;
    created_at: string;
    started_at: string | null;
    finished_at: string | null;
    workflow_name: string | null;
  }>;
  widget_tokens: Array<{
    id: string;
    token_prefix: string;
    workflow_name: string;
    created_at: string;
    expires_at: string;
    revoked_at: string | null;
  }>;
  audit_events: Array<{
    id: string;
    event_type: string;
    metadata: Record<string, unknown> | null;
    created_at: string;
  }>;
};

export type UserLinkageRow = {
  email: string;
  id: string;
  invitation_status: string | null;
  invited_at: string | null;
  last_run_at: string | null;
  linked_vehicles: number;
  recent_run_status: string | null;
  total_runs: number;
  workflows: number;
};

type RequestOptions = {
  body?: Record<string, unknown>;
  method?: "GET" | "POST";
  mfaToken?: string | null;
  session: Session;
};

export async function adminRequest<T>(path: string, options: RequestOptions) {
  const { data, error } = await supabase.functions.invoke("admin-api" + path, {
    body: options.body,
    headers: {
      Authorization: `Bearer ${options.session.access_token}`,
      ...(options.mfaToken ? { "x-admin-mfa-token": options.mfaToken } : {})
    },
    method: options.method ?? "GET"
  });

  if (error) throw new Error(await functionErrorMessage(error, "Admin request failed."));
  return data as T;
}

export function readMfaToken() {
  return window.sessionStorage.getItem("tesflows_admin_mfa_token");
}

export function saveMfaToken(token: string) {
  window.sessionStorage.setItem("tesflows_admin_mfa_token", token);
}

export function clearMfaToken() {
  window.sessionStorage.removeItem("tesflows_admin_mfa_token");
}

async function functionErrorMessage(error: unknown, fallback: string) {
  const context = error && typeof error === "object" && "context" in error ? (error as { context?: unknown }).context : null;
  if (context instanceof Response) {
    const text = await context.text().catch(() => "");
    if (text) {
      try {
        const parsed = JSON.parse(text) as { error?: unknown; message?: unknown };
        if (typeof parsed.error === "string") return parsed.error;
        if (typeof parsed.message === "string") return parsed.message;
      } catch {
        return text;
      }
    }
  }

  return error instanceof Error ? error.message : fallback;
}
